/**
 * City of Signals - Main Game Component
 * Character-driven strategy game experience
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { buildWebSocketUrl } from '../../config/api';
import CityMap from './CityMap';
import SignalCard from './SignalCard';
import VoicesPanel from './VoicesPanel';
import ChoiceButtons from './ChoiceButtons';
import MetersHUD from './MetersHUD';
import AuraPanel from './AuraPanel';
import ReflectionCard from './ReflectionCard';
import LockedGateScreen from './LockedGateScreen';

const CityOfSignalsGame = ({ user }) => {
  const { level } = useParams();
  const navigate = useNavigate();
  
  const [unlocks, setUnlocks] = useState(null);
  const [session, setSession] = useState(null);
  const [currentTurn, setCurrentTurn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReflection, setShowReflection] = useState(false);
  const [lastChoiceResult, setLastChoiceResult] = useState(null);
  const wsRef = useRef(null);

  useEffect(() => {
    loadUnlocks();
    if (level) {
      startNewSession(level);
    }
    
    connectGameNotifications();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [level, user]);

  const connectGameNotifications = useCallback(() => {
    if (!user?.user_id) return;
    
    try {
      const wsUrl = buildWebSocketUrl(
        `/api/game/city-of-signals/ws/game-notifications/${user.user_id}`,
      );
      
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        console.log('Game notifications WebSocket connected');
        wsRef.current = ws;
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'ping') {
            ws.send(JSON.stringify({ type: 'pong' }));
            return;
          }
          
          if (data.type === 'pong') {
            return;
          }
          
          if (data.type === 'game_unlock' || data.type === 'game_milestone') {
            console.log('Game notification:', data.message);
            alert(data.message);
          }
        } catch (err) {
          console.error('Error parsing WebSocket message:', err);
        }
      };
      
      ws.onclose = () => {
        console.log('Game notifications WebSocket disconnected');
        setTimeout(() => {
          if (user?.user_id) {
            connectGameNotifications();
          }
        }, 5000);
      };
      
      ws.onerror = (err) => {
        console.error('WebSocket error:', err);
      };
    } catch (err) {
      console.error('Error connecting to game notifications:', err);
    }
  }, [user]);

  const loadUnlocks = async () => {
    try {
      const response = await axios.get('/api/game/city-of-signals/unlocks');
      setUnlocks(response.data.unlocks);
    } catch (err) {
      console.error('Error loading unlocks:', err);
    }
  };

  const startNewSession = async (levelName) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`/api/game/city-of-signals/levels/${levelName}/session/new`);
      setSession(response.data.session);
      setCurrentTurn(response.data.current_turn);
    } catch (err) {
      if (err.response?.status === 403) {
        setError({
          type: 'LOCKED',
          detail: err.response.data.detail
        });
      } else {
        setError({ type: 'ERROR', message: err.response?.data?.detail || 'Failed to start session' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChoice = async (choice) => {
    if (!session || !currentTurn) return;
    
    try {
      const choiceMap = {
        'trust_A': 'trust_a',
        'trust_B': 'trust_b',
        'wait': 'wait',
        'blend': 'blend'
      };
      
      const backendChoice = choiceMap[choice] || choice;
      
      const response = await axios.post(
        `/api/game/city-of-signals/session/${session.session_id}/turn/choice`,
        {
          session_id: session.session_id,
          choice: backendChoice,
          turn_number: currentTurn.turn_number
        }
      );
      
      setLastChoiceResult(response.data.result);
      setShowReflection(true);
      
      if (response.data.next_turn) {
        setCurrentTurn(response.data.next_turn);
      }
      
      if (response.data.session_complete) {
        setTimeout(() => {
          navigate('/game/city-of-signals');
        }, 5000);
      }
    } catch (err) {
      console.error('Error making choice:', err);
      setError({ type: 'ERROR', message: 'Failed to process choice' });
    }
  };

  const handleContinue = () => {
    setShowReflection(false);
    setLastChoiceResult(null);
  };

  const levelTitle =
    level === 'level_1'
      ? 'Reading the Room'
      : level === 'level_2'
        ? 'Managing Tradeoffs'
        : 'System Mastery';

  const totalTurns = level === 'level_1' ? 3 : 5;

  if (loading) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <p className="text-body text-secondary">Loading City of Lights…</p>
      </div>
    );
  }

  if (error?.type === 'LOCKED') {
    return <LockedGateScreen error={error} unlocks={unlocks} level={level} />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center p-4">
        <p className="text-body text-danger">Error: {error.message}</p>
      </div>
    );
  }

  if (!session || !currentTurn) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <p className="text-body text-secondary">No active session</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone text-primary relative overflow-hidden">
      <MetersHUD meters={session.meters} />
      
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <CityMap 
          activeDistrict={currentTurn.district}
          level={level}
        />
      </div>
      
      <div className="relative z-10 container mx-auto px-4 py-8 pt-28">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-6 pb-4 border-b border-hairline">
            <p className="text-label text-pine mb-2">
              Turn <span className="figure">{currentTurn.turn_number}</span> of{' '}
              <span className="figure">{totalTurns}</span>
            </p>
            <h2 className="font-display text-h2 text-primary">{levelTitle}</h2>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <SignalCard signal={currentTurn} />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-6"
          >
            <VoicesPanel voices={currentTurn.voices} />
          </motion.div>

          {!showReflection && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="mt-8"
            >
              <ChoiceButtons
                voices={currentTurn.voices}
                onChoice={handleChoice}
                level={level}
              />
            </motion.div>
          )}

          <AnimatePresence>
            {showReflection && lastChoiceResult && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mt-8"
              >
                <ReflectionCard
                  turn={lastChoiceResult.turn}
                  session={lastChoiceResult.session}
                  onContinue={handleContinue}
                  onSaveInsight={() => {
                    console.log('Save insight');
                  }}
                  onShareCommunity={() => {
                    console.log('Share to community');
                  }}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <AuraPanel
        turn={currentTurn}
        session={session}
        showReflection={showReflection}
      />
    </div>
  );
};

export default CityOfSignalsGame;
